import React from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://businessdevelopmentauthority.net';

const AIStrategyPage: React.FC = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/ai-strategy`,
        'url': `${SITE_URL}/ai-strategy`,
        'name': 'AI Strategy Development - Business Development Authority',
        'description': 'Transform your business with comprehensive AI strategy consulting that aligns technology with your business objectives. Expert roadmap development and change management.',
        'isPartOf': { '@id': SITE_URL },
        'about': { '@type': 'Service', 'name': 'AI Strategy Development' },
      },
      {
        '@type': 'Service',
        'name': 'AI Strategy Development',
        'url': `${SITE_URL}/ai-strategy`,
        'description': 'Comprehensive AI strategy consulting including discovery, roadmap development, and change management for organizations seeking measurable AI outcomes.',
        'serviceType': 'AI Strategy Consulting',
        'provider': {
          '@type': 'Organization',
          'name': 'Business Development Authority',
          'url': SITE_URL,
        },
        'areaServed': 'US',
        'hasOfferCatalog': {
          '@type': 'OfferCatalog',
          'name': 'AI Strategy Services',
          'itemListElement': [
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Discovery & Assessment' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Roadmap Development' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Change Management' } },
          ],
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/4276d05f7c390a9d464adccff2693d13.jpg')` }}>
          <div className="max-w-7xl mx-auto px-6 text-center text-white w-full">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">AI Strategy Development</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">Transform your business with comprehensive AI strategy consulting that aligns technology with your business objectives</p>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Start Your AI Journey
            </button>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Strategic AI Planning for Business Growth</h2>
                <p className="text-lg text-gray-700 mb-6">
                  Our AI Strategy Development service helps organizations create comprehensive roadmaps for AI adoption and implementation. We work closely with your leadership team to identify opportunities, assess readiness, and develop actionable plans that drive measurable business outcomes.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  From initial assessment to full-scale implementation planning, we ensure your AI strategy aligns with your business goals, industry requirements, and organizational capabilities.
                </p>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden shadow-2xl">
                <img src="https://readdy.ai/api/search-image?query=Business%20professionals%20collaborating%20on%20AI%20strategy%20planning%20with%20digital%20tablets%20and%20interactive%20displays%2C%20modern%20office%20setting%20with%20data%20visualizations%20and%20strategic%20planning%20materials%2C%20professional%20corporate%20atmosphere&width=600&height=400&seq=ai-strategy-overview&orientation=landscape" alt="AI Strategy Planning" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Key Components Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Our Strategic Approach</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-search-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Discovery & Assessment</h3>
                <p className="text-gray-700">Comprehensive analysis of your current state, business objectives, and AI readiness to identify strategic opportunities and potential challenges.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-roadmap-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Roadmap Development</h3>
                <p className="text-gray-700">Creation of detailed implementation roadmaps with clear milestones, resource requirements, and success metrics aligned with your business goals.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-team-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Change Management</h3>
                <p className="text-gray-700">Strategic planning for organizational change, including stakeholder engagement, training programs, and cultural transformation initiatives.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Strategic Benefits</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Aligned Business Objectives</h3>
                  <p className="text-gray-700">Ensure AI initiatives directly support your strategic business goals and deliver measurable ROI.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Risk Mitigation</h3>
                  <p className="text-gray-700">Identify and address potential challenges before they impact your AI implementation success.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Resource Optimization</h3>
                  <p className="text-gray-700">Maximize the value of your AI investments through strategic planning and prioritization.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Competitive Advantage</h3>
                  <p className="text-gray-700">Stay ahead of industry trends with forward-thinking AI strategies that drive innovation.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Develop Your AI Strategy?</h2>
            <p className="text-xl mb-8">Let's create a comprehensive AI roadmap tailored to your business objectives and industry requirements.</p>
            <button onClick={() => window.REACT_APP_NAVIGATE('/calendar')} className="whitespace-nowrap bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Schedule a Strategy Session
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AIStrategyPage;
